import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, Trophy, X, HelpCircle } from "lucide-react";

/**
 * Nonogram (Picross) Game
 * 
 * Rules:
 * - Grid of cells (Filled or Empty).
 * - Clues on top/left indicate lengths of consecutive filled blocks.
 * - Player must deduce the grid.
 */
const NonogramGame = () => {
    const navigate = useNavigate();

    // --- Config ---
    const [difficulty, setDifficulty] = useState("Easy"); // Easy (5x5), Medium (10x10)
    
    // --- State ---
    const [solution, setSolution] = useState([]);
    const [grid, setGrid] = useState([]); // User's grid: 0=Empty, 1=Filled, 2=Crossed
    const [rowClues, setRowClues] = useState([]);
    const [colClues, setColClues] = useState([]);
    const [gameStatus, setGameStatus] = useState("playing"); // playing, won
    
    const [showRules, setShowRules] = useState(false);
    const [showViolation, setShowViolation] = useState(true);

    // --- Logic ---

    const generatePuzzle = (diff) => {
        const size = diff === "Easy" ? 5 : 10;
        const newSolution = [];
        
        // 1. Generate Random Grid
        for(let r=0; r<size; r++) {
            const row = [];
            for(let c=0; c<size; c++) {
                // ~60% fill rate usually makes for decent puzzles
                row.push(Math.random() < 0.6 ? 1 : 0);
            }
            newSolution.push(row);
        }

        // Ensure not empty
        if (newSolution.every(r => r.every(c => c === 0))) {
            newSolution[0][0] = 1;
        }

        // 2. Calculate Clues
        const rClues = newSolution.map(row => {
            const clues = [];
            let count = 0;
            for(let val of row) {
                if(val === 1) count++;
                else if(count > 0) { clues.push(count); count = 0; }
            }
            if(count > 0) clues.push(count);
            return clues.length > 0 ? clues : [0];
        });

        const cClues = [];
        for(let c=0; c<size; c++) {
            const clues = [];
            let count = 0;
            for(let r=0; r<size; r++) {
                if(newSolution[r][c] === 1) count++;
                else if(count > 0) { clues.push(count); count = 0; }
            }
            if(count > 0) clues.push(count);
            cClues.push(clues.length > 0 ? clues : [0]);
        }

        setSolution(newSolution);
        setRowClues(rClues);
        setColClues(cClues);
        setGrid(Array(size).fill(null).map(() => Array(size).fill(0)));
        setGameStatus("playing");
    };

    useEffect(() => {
        generatePuzzle(difficulty);
    }, [difficulty]);


    // --- Validation Helper ---
    // Checks if a given line (array of 0, 1, 2, 3) is consistent with the clues.
    // 0=Empty(Unknown), 1=Filled, 2=Cross(Known Empty), 3=Error(Filled)
    const isValidLine = (line, clues) => {
        // Treat '3' (Error) as '1' (Filled) for consistency checking
        const cleanLine = line.map(c => c === 3 ? 1 : c); 
        
        // Memoization key: `${index}-${clueIndex}`
        const memo = new Map();

        const check = (idx, clueIdx) => {
            const key = `${idx}-${clueIdx}`;
            if (memo.has(key)) return memo.get(key);

            // Base Case: Reached end of line
            if (idx >= cleanLine.length) {
                // Valid if we used all clues
                return clueIdx === clues.length;
            }

            let res = false;

            // Option 1: Treat current cell as EMPTY (skip)
            // Allowed if current cell is NOT 1 (Filled)
            if (cleanLine[idx] !== 1) {
                if (check(idx + 1, clueIdx)) res = true;
            }

            // Option 2: Treat current cell as start of BLOCK
            // Allowed if:
            // - We have clue available
            // - Block fits in remaining space
            // - Block space does not contain Known Empty (2)
            // - Cell after block is not Filled (1)
            // - Current cell is NOT Known Empty (2)
            if (!res && clueIdx < clues.length && cleanLine[idx] !== 2) {
                const len = clues[clueIdx];
                if (idx + len <= cleanLine.length) {
                    // Check if block can be placed (no 2s)
                    let canPlace = true;
                    for (let i = 0; i < len; i++) {
                        if (cleanLine[idx + i] === 2) {
                            canPlace = false;
                            break;
                        }
                    }

                    // Check neighbor after block
                    if (canPlace && idx + len < cleanLine.length) {
                        if (cleanLine[idx + len] === 1) canPlace = false;
                    }

                    if (canPlace) {
                        // Recurse: skip block len + 1 (gap)
                        if (check(idx + len + 1, clueIdx + 1)) res = true;
                    }
                }
            }

            memo.set(key, res);
            return res;
        };

        return check(0, 0);
    };

    const handleCellClick = (r, c, isRightClick) => {
        if (gameStatus !== "playing") return;

        // Clone grid
        const newGrid = grid.map(row => [...row]);
        const currentVal = newGrid[r][c];

        if (isRightClick) {
            // Toggle Cross
            if (currentVal === 0) newGrid[r][c] = 2; // Empty -> Cross
            else if (currentVal === 2) newGrid[r][c] = 0; // Cross -> Empty
            // Note: If cell is 1 or 3, do nothing (must unfill first)
        } else {
            // Toggle Fill
            if (currentVal === 1 || currentVal === 3) {
                newGrid[r][c] = 0; // Fill/Error -> Empty
            } else if (currentVal === 0) {
                // Try Filling
                newGrid[r][c] = 1;
                
                // Validate Row and Column with this new fill
                const rowLine = newGrid[r];
                const colLine = newGrid.map(row => row[c]); // Extract column

                const isRowValid = isValidLine(rowLine, rowClues[r]);
                const isColValid = isValidLine(colLine, colClues[c]);

                if (!isRowValid || !isColValid) {
                    newGrid[r][c] = 3; // Mark as Error
                }
            }
            // If currentVal === 2 (Cross), do nothing. Must unmark first.
        }

        setGrid(newGrid);
        checkWin(newGrid);
    };

    const checkWin = (currentGrid) => {
        // Helper to extract clues from a line
        const getLineClues = (line) => {
            const clues = [];
            let count = 0;
            for(let val of line) {
                // Treat 1 (Filled) and 3 (Error-Filled) as Filled for checking pattern
                if(val === 1 || val === 3) count++;
                else if(count > 0) { clues.push(count); count = 0; }
            }
            if(count > 0) clues.push(count);
            return clues.length > 0 ? clues : [0];
        };

        const arraysEqual = (a1, a2) => {
            if (a1.length !== a2.length) return false;
            for (let i = 0; i < a1.length; i++) {
                if (a1[i] !== a2[i]) return false;
            }
            return true;
        };

        const size = currentGrid.length;

        // Check Rows
        for(let r=0; r<size; r++) {
            const currentClues = getLineClues(currentGrid[r]);
            if (!arraysEqual(currentClues, rowClues[r])) return;
        }

        // Check Cols
        for(let c=0; c<size; c++) {
            const colLine = currentGrid.map(row => row[c]);
            const currentClues = getLineClues(colLine);
            if (!arraysEqual(currentClues, colClues[c])) return;
        }

        setGameStatus("won");
    };


    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center py-8">
            {/* Header */}
            <div className="w-full max-w-2xl px-4 mb-8 flex items-center justify-between">
                <button
                    onClick={() => navigate("/games")}
                    className="flex items-center text-blue-600 hover:text-blue-800 font-medium"
                >
                    <ArrowLeft className="h-5 w-5 mr-1" /> 
                    Back to Games
                </button>
                <div className="text-xl font-bold text-gray-800">Nonograms</div>
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowRules(!showRules)}
                        className={`p-2 rounded transition-colors ${showRules ? "text-blue-600 bg-blue-50" : "text-gray-500 hover:text-blue-600"}`}
                        title="How to Play"
                    >
                        <HelpCircle className="h-5 w-5" />
                    </button>
                    <button
                        onClick={() => generatePuzzle(difficulty)}
                        className="p-2 text-gray-500 hover:text-blue-600"
                        title="Restart"
                    >
                         <RefreshCw className="h-5 w-5" />
                    </button>
                </div>
            </div>

            {/* Rules Section */}
            {showRules && (
                <div className="max-w-xl w-full bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 text-blue-900 animate-fade-in shadow-sm">
                    <h3 className="font-bold text-lg mb-3">How to Play Nonograms</h3>
                    <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">
                        <li><strong>Goal:</strong> Fill in the grid cells to match the hidden pattern.</li>
                        <li><strong>The Numbers:</strong> The numbers at the top and left tell you how many <strong>consecutive</strong> filled squares are in that row or column.</li>
                        <li><strong>Example:</strong> A clue of <strong>"3 1"</strong> means there is a block of <strong>3</strong> filled squares, followed by at least one empty square, followed by <strong>1</strong> filled square.</li>
                        <li><strong>Controls:</strong> 
                            <ul className="list-circle pl-5 mt-1">
                                <li><strong>Left Click:</strong> Fill a square.</li>
                                <li><strong>Right Click:</strong> Mark a square with 'X' (empty).</li>
                            </ul>
                        </li>
                    </ul>
                    <button 
                        onClick={() => setShowRules(false)}
                        className="mt-4 text-xs font-bold text-blue-600 hover:text-blue-800 uppercase tracking-wide"
                    >
                        Close Rules
                    </button>
                </div>
            )}

            {/* Controls */}
            <div className="flex gap-4 mb-6">
                 <div className="bg-white rounded-lg p-1 flex shadow-sm border border-gray-200">
                     <button 
                        onClick={() => setDifficulty("Easy")}
                        className={`px-4 py-1 rounded text-sm font-medium transition-colors ${difficulty === "Easy" ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}
                     >
                         Easy (5x5)
                     </button>
                     <button 
                        onClick={() => setDifficulty("Medium")}
                        className={`px-4 py-1 rounded text-sm font-medium transition-colors ${difficulty === "Medium" ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-50"}`}
                     >
                         Medium (10x10)
                     </button>
                 </div>

                 <label className="flex items-center space-x-2 text-sm text-gray-700 bg-white px-3 py-2 rounded-lg shadow-sm border border-gray-200 cursor-pointer hover:bg-gray-50">
                    <input 
                        type="checkbox" 
                        checked={showViolation} 
                        onChange={(e) => setShowViolation(e.target.checked)}
                        className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                    />
                    <span>Show Violations</span>
                 </label>
            </div>

            {/* Game Board */}
            <div className="bg-white p-4 rounded-xl shadow-xl border border-gray-200 overflow-auto select-none" onContextMenu={(e) => e.preventDefault()}>
                 {gameStatus === "won" && (
                    <div className="absolute inset-0 bg-white/80 z-10 flex flex-col items-center justify-center animate-fade-in backdrop-blur-sm rounded-xl">
                        <Trophy className="w-20 h-20 text-yellow-500 mb-4" />
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Puzzle Solved!</h2>
                        <button 
                             onClick={() => generatePuzzle(difficulty)}
                             className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-lg font-medium"
                        >
                            Next Puzzle
                        </button>
                    </div>
                 )}

                 <div className="flex">
                      {/* Top-Left Empty Space */}
                      <div className="flex-shrink-0 flex flex-col justify-end pr-2 border-r-2 border-gray-300">
                          {/* Row Clues Container is Vertical below, but this spacer matches Col Clues height */}
                          <div className="h-24 w-20 flex items-end justify-end pb-2 font-mono text-xs text-gray-400">
                              {/* Optional: Axis labels */}
                          </div>
                      </div>

                      {/* Column Clues */}
                      <div className="flex">
                           {colClues.map((clue, i) => (
                               <div key={i} className="w-8 sm:w-10 flex flex-col justify-end items-center pb-2 bg-gray-50 border-r border-gray-100 gap-1 h-24">
                                   {clue.map((num, idx) => (
                                       <span key={idx} className="font-bold text-gray-700 text-sm">{num}</span>
                                   ))}
                               </div>
                           ))}
                      </div>
                 </div>

                 <div className="flex">
                      {/* Row Clues */}
                      <div className="flex-col w-20 flex-shrink-0 border-r-2 border-gray-300">
                           {rowClues.map((clue, i) => (
                               <div key={i} className="h-8 sm:h-10 flex items-center justify-end pr-2 bg-gray-50 border-b border-gray-100 gap-2">
                                     {clue.map((num, idx) => (
                                       <span key={idx} className="font-bold text-gray-700 text-sm">{num}</span>
                                   ))}
                               </div>
                           ))}
                      </div>

                      {/* Grid */}
                      <div className="border-t-2 border-l-2 border-gray-300">
                          {grid.map((row, r) => (
                              <div key={r} className="flex">
                                  {row.map((cell, c) => (
                                      <button
                                          key={`${r}-${c}`}
                                          onClick={() => handleCellClick(r, c, false)}
                                          onContextMenu={(e) => { e.preventDefault(); handleCellClick(r, c, true); }}
                                          className={`
                                              w-8 h-8 sm:w-10 sm:h-10 border-b border-r border-gray-200 flex items-center justify-center
                                              transition-colors duration-150
                                              ${cell === 1 ? "bg-gray-800" : (cell === 3 && showViolation) ? "bg-red-500" : (cell === 3 && !showViolation) ? "bg-gray-800" : "bg-white hover:bg-gray-50"}
                                              ${(c + 1) % 5 === 0 && c !== grid.length - 1 ? "border-r-2 border-r-gray-300" : ""}
                                              ${(r + 1) % 5 === 0 && r !== grid.length - 1 ? "border-b-2 border-b-gray-300" : ""}
                                          `}
                                      >
                                          {cell === 2 && <X className="w-5 h-5 text-red-400" />}
                                      </button>
                                  ))}
                              </div>
                          ))}
                      </div>
                 </div>
            </div>
            
            <div className="mt-6 text-gray-500 text-sm max-w-md text-center">
                 Pro Tip: Right-click a square to mark it with an X!
            </div>
        </div>
    );
};

export default NonogramGame;
